import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Monitor, Plus, RefreshCw, Trash2, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/pm/ConfirmDialog';
import {
  deregisterDevice,
  generatePairingCode,
  getGates,
  getAllDevices,
} from '@/services/api.client';
import type { Gate, GuardDevice } from '@/types';

interface Props {
  propertyId: string;
  isPm: boolean;
}

/**
 * DevicesTab — PM dashboard tab showing registered gate devices.
 * Allows pairing new devices and deregistering existing ones.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7
 */
export function DevicesTab({ propertyId, isPm }: Props) {
  const qc = useQueryClient();
  const [pairingOpen, setPairingOpen] = useState(false);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpiry, setPairingExpiry] = useState<string | null>(null);
  const [selectedGateId, setSelectedGateId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deregisterTarget, setDeregisterTarget] = useState<GuardDevice | null>(null);

  const devicesQuery = useQuery({
    queryKey: ['pm', 'devices', propertyId],
    queryFn: () => getAllDevices(propertyId),
    enabled: !!propertyId,
    refetchInterval: 30_000,
  });

  const gatesQuery = useQuery({
    queryKey: ['gates', propertyId],
    queryFn: () => getGates(propertyId),
    enabled: pairingOpen,
  });

  const pairMutation = useMutation({
    mutationFn: () =>
      generatePairingCode(propertyId, {
        gateId: selectedGateId,
        ...(deviceName ? { deviceName } : {}),
      }),
    onSuccess: (data) => {
      setPairingCode(data.code);
      setPairingExpiry(data.expiresAt);
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Failed to generate pairing code');
    },
  });

  const deregisterMutation = useMutation({
    mutationFn: (deviceSessionId: string) => deregisterDevice(propertyId, deviceSessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'devices', propertyId] });
      toast.success('Device deregistered');
    },
    onError: () => {
      toast.error('Failed to deregister device');
    },
  });

  const handleGenerateCode = () => {
    if (!selectedGateId) {
      toast.error('Please select a gate');
      return;
    }
    pairMutation.mutate();
  };

  const handleClosePairing = () => {
    setPairingOpen(false);
    setPairingCode(null);
    setPairingExpiry(null);
    setSelectedGateId('');
    setDeviceName('');
  };

  const isDeviceInactive = (lastSeenAt: string | null) => {
    if (!lastSeenAt) return true;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return new Date(lastSeenAt).getTime() < sevenDaysAgo;
  };

  const devices: GuardDevice[] = devicesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">
          Registered gate devices for this property.
        </p>
        {isPm && (
          <Button
            size="sm"
            className="gap-1.5"
            style={{ background: 'var(--cta-primary-bg)', color: 'var(--text-inverse)' }}
            onClick={() => setPairingOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Register Gate Device
          </Button>
        )}
      </div>

      {/* Device list */}
      {devicesQuery.isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      )}

      {devices.length === 0 && !devicesQuery.isLoading && (
        <div className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-8 text-center">
          <Monitor className="mx-auto size-10 text-[var(--text-tertiary)] mb-3" />
          <p className="text-sm text-[var(--text-secondary)]">No devices registered yet.</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">
            Click &quot;Register Gate Device&quot; to pair a tablet to a gate.
          </p>
        </div>
      )}

      {devices.length > 0 && (
        <div className="space-y-2">
          {devices.map((device) => {
            const inactive = isDeviceInactive(device.lastSeenAt);
            return (
              <div
                key={device.id}
                className="flex items-center justify-between rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-full bg-[var(--bg-surface-raised)]">
                    {inactive ? (
                      <WifiOff className="size-4 text-[var(--status-amber-text)]" />
                    ) : (
                      <Wifi className="size-4 text-[var(--status-green-text)]" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {device.deviceName ?? 'Gate Device'}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      Last seen:{' '}
                      {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : 'Never'}
                      {inactive && (
                        <span className="ml-2 text-[var(--status-amber-text)]">⚠ Inactive</span>
                      )}
                    </p>
                  </div>
                </div>
                {isPm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[var(--text-error)] hover:text-[var(--text-error)]"
                    onClick={() => setDeregisterTarget(device)}
                    disabled={deregisterMutation.isPending}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pairing Code Dialog */}
      <Dialog open={pairingOpen} onOpenChange={handleClosePairing}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Register Gate Device</DialogTitle>
          </DialogHeader>

          {!pairingCode ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label
                  htmlFor="pair-gate"
                  className="text-sm font-medium text-[var(--text-primary)]"
                >
                  Select Gate
                </label>
                {gatesQuery.isLoading ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <select
                    id="pair-gate"
                    value={selectedGateId}
                    onChange={(e) => setSelectedGateId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select a gate…</option>
                    {((gatesQuery.data as Gate[]) ?? []).map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="pair-name"
                  className="text-sm font-medium text-[var(--text-primary)]"
                >
                  Device Name <span className="text-[var(--text-tertiary)]">(optional)</span>
                </label>
                <Input
                  id="pair-name"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="Main Gate Tablet"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleClosePairing}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateCode}
                  disabled={!selectedGateId || pairMutation.isPending}
                >
                  {pairMutation.isPending && <RefreshCw className="mr-2 size-4 animate-spin" />}
                  Generate Code
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-4 text-center">
              <p className="text-sm text-[var(--text-secondary)]">
                Enter this code on the tablet to pair it:
              </p>
              <p className="text-4xl font-mono font-bold tracking-[0.3em] text-[var(--text-primary)]">
                {pairingCode}
              </p>
              <p className="text-xs text-[var(--text-tertiary)]">
                Expires:{' '}
                {pairingExpiry ? new Date(pairingExpiry).toLocaleTimeString() : '5 minutes'}
              </p>
              <DialogFooter className="justify-center">
                <Button variant="outline" onClick={handleClosePairing}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deregister confirmation */}
      <ConfirmDialog
        open={!!deregisterTarget}
        title="Deregister Device"
        description={`Are you sure you want to deregister "${deregisterTarget?.deviceName ?? 'this device'}"? It will need to be re-paired to this gate.`}
        confirmLabel="Deregister"
        loading={deregisterMutation.isPending}
        onConfirm={() => {
          if (deregisterTarget) deregisterMutation.mutate(deregisterTarget.id);
          setDeregisterTarget(null);
        }}
        onCancel={() => setDeregisterTarget(null)}
      />
    </div>
  );
}
